<template>
  <div>
    <LayoutDashboard>
      <div
        v-if="isLoading"
        class="box bg-white border-radius pt-5 text-center"
      >
        <div class="spinner-border spinner-border-sm">
          <span class="sr-only">
            Loading...
          </span>
        </div>
      </div>
      <div
        v-if="!isLoading"
        class="box bg-white border-radius p-4"
      >
        <div class="row">
          <div class="col-md-5 col-lg-4 col-xl-3 p-0">
            <img
              v-if="!userData.avatar"
              class="image-width"
              src="@/assets/icon/guest.png"
              alt="profile-icon"
            >
            <img
              v-if="userData.avatar"
              class="image-width rounded-circle"
              :src="userData.avatar"
              alt="profile-icon"
            >
          </div>
          <div class="col-md-8 col-lg-8 col-xl-9 pt-3 pt-lg-5">
            <div class="text-size-one font-weight-bold">
              {{ userData.fullname || 'N/A' }}
            </div>
            <div class="mt-2 text-secondary">
              {{ userData.role.name || 'N/A' }}
            </div>
            <div class="row m-0 p-0 mt-4 col-lg-8 col-xl-6 text-center">
              <div class="col-6 p-0 pr-2">
                <router-link
                  v-if="permission.permissions.includes('edit')"
                  class="btn-info border border-info rounded cursor-pointer p-2"
                  tag="div"
                  to="update-profile"
                >
                  <small>{{ $t('user-profile.button.edit') }}</small>
                </router-link>
              </div>
              <div class="col-6 p-0 pl-2">
                <div
                  v-if="!userData.isActiveDirectory"
                  data-toggle="modal"
                  data-target="#modalPassword"
                  class="btn-outline-info border border-info cursor-pointer rounded p-2"
                  @click="showModal('password')"
                >
                  <small>{{ $t('user-profile.button.password') }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row m-0 p-0 mt-4">
          <div class="col-md-6 col-xl-3 p-0 px-2 text-truncate">
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.fullname') }}</small>
              </div>
              <div>
                <small>{{ userData.fullname || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.work-location') }}</small>
              </div>
              <div>
                <small>{{ userData.workLocationActual || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.age') }}</small>
              </div>
              <div>
                <small>{{ age || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.address') }}</small>
              </div>
              <div class="text-wrap">
                <small>{{ userData.address || 'N/A' }}</small>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-xl-3 p-0 px-2 text-truncate">
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.username') }}</small>
              </div>
              <div>
                <small>{{ userData.username || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.organization') }}</small>
              </div>
              <div>
                <small>{{ userData.workLocation.name || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.gender') }}</small>
              </div>
              <div>
                <small v-if="userData.gender">{{ $t('user-profile.form.male') }}</small>
                <small v-if="userData.gender === false">{{ $t('user-profile.form.female') }}</small>
                <small v-if="userData.gender === null">N/A</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.department') }}</small>
              </div>
              <div>
                <small>{{ userData.workUnit || 'N/A' }}</small>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-xl-3 p-0 px-2 text-truncate">
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.email') }}</small>
              </div>
              <div>
                <small>{{ userData.email || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.company') }}</small>
              </div>
              <div>
                <small>{{ userData.company.name || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.nik') }}</small>
              </div>
              <div>
                <small>{{ userData.nric || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.comorbid') }}</small>
              </div>
              <div v-if="userData.comorbid">
                <small>{{ $t('user-profile.comorbid.true') }}</small>
              </div>
              <div v-if="!userData.comorbid">
                <small>{{ $t('user-profile.comorbid.false') }}</small>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-xl-3 p-0 px-2 text-truncate">
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.birthdate') }}</small>
              </div>
              <div>
                <small>{{ userData.birthdate || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.role') }}</small>
              </div>
              <div>
                <small>{{ userData.role.name || 'N/A' }}</small>
              </div>
            </div>
            <div class="mb-3">
              <div>
                <small class="font-weight-bold">{{ $t('user-profile.label.phone') }}</small>
              </div>
              <div>
                <small>{{ userData.phoneNumber || 'N/A' }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutDashboard>
    <ModalPassword
      :id="userData.id"
    />
  </div>
</template>

<script>
import Api from '@/utils/Api'
import LayoutDashboard from '@/components/LayoutDashboard'
import { format, differenceInYears } from 'date-fns'
import ModalPassword from '@/components/modal/password/ModalPassword'

export default {
  name: 'UserProfile',

  components: {
    LayoutDashboard,
    ModalPassword
  },

  data () {
    return {
      userData: {
        company: {
          id: '',
          name: ''
        },
        workLocation: {
          name: ''
        },
        role: {
          name: ''
        }
      },
      age: '',
      modal: {
        password: false
      },
      isLoading: false,
      permission: {
        permissions: []
      }
    }
  },

  async created () {
    this.getPermission()
    await this.getDataUser()
  },

  methods: {
    getPermission () {
      const dataLoc = JSON.parse(localStorage.getItem('profile'))
      const index = dataLoc.role.permissions.findIndex(element => element.category === 'profile')
      this.permission = dataLoc.role.permissions[index]
    },
    async getDataUser () {
      this.isLoading = true
      const dataLogin = JSON.parse(localStorage.getItem('profile'))
      const data = await Api.users.get(dataLogin.id)
      this.userData = data.data
      this.checkNull('userData', 'company')
      this.checkNull('userData', 'workLocation')
      if (this.userData.birthdate) {
        this.age = differenceInYears(new Date(), new Date(this.userData.birthdate))
        this.userData.birthdate = format(new Date(this.userData.birthdate), 'dd-MMM-yyyy')
      }
      this.isLoading = false
    },
    checkNull (parent, child) {
      if (!this[parent][child]) {
        this[parent][child] = {
          id: null,
          name: null
        }
      }
    },
    showModal (modalname) {
      this.modal[modalname] = true
    },
    closeModal (modalname) {
      this.modal[modalname] = false
      this.$router.push({
        name: 'user-profile'
      })
    }
  }
}
</script>

<style scoped>
.box {
  height: 587px;
  overflow: auto;
}
.bg {
  background-color: #f5efff;
  min-height: 750px;
}
.border-radius {
  border-radius: 11px;
}
.text-size-one {
  font-size: 20px;
}
.cursor-pointer {
  cursor: pointer !important;
}
.image-width {
  width: 200px;
  height: 200px;
  object-fit: cover;
}

@media screen and (max-width: 576px) {
  .bg {
    min-height: 1600px;
  }
}
@media screen and (max-width: 768px) {
  .bg {
    min-height: 800px;
  }
}
@media screen and (max-width: 1024px) {
  .bg {
    min-height: 900px;
  }
}
</style>
